import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, HandCoins, Loader2, Plus, Search, X } from 'lucide-react';
import type {
  HrMeResponse, ReimbursementCandidate, ReimbursementItem, ReimbursementStatus,
} from '@ip/shared-types';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatDateMedium } from '@/lib/hr';
import { t } from '@/i18n';

const STATUS_CHIP: Record<ReimbursementStatus, string> = {
  pending_approval: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  approved: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400',
  rejected: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  exported: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

const formatPln = (v: number) =>
  `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN`;

function StatusChip({ status }: { status: ReimbursementStatus }) {
  return (
    <Badge variant="outline" className={cn('font-normal whitespace-nowrap', STATUS_CHIP[status])}>
      {t(`hr.reimbursements.status.${status}`)}
    </Badge>
  );
}

/**
 * Employee expense reimbursements: the request wizard (NIP+gross, fallback
 * issue-date+gross), the caller's own requests, and — for managers/admins —
 * the approval queue.
 */
export function ReimbursementsView({ nip, hrMe }: { nip: string; hrMe: HrMeResponse | null }) {
  const {
    searchReimbursements, createReimbursement, myReimbursements,
    reimbursementApprovals, approveReimbursement, rejectReimbursement,
  } = useHrApi();

  const [mine, setMine] = useState<ReimbursementItem[] | null>(null);
  const [approvals, setApprovals] = useState<ReimbursementItem[]>([]);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchNip, setSearchNip] = useState('');
  const [searchGross, setSearchGross] = useState('');
  const [byDate, setByDate] = useState(false);
  const [searchDate, setSearchDate] = useState('');
  const [searched, setSearched] = useState(false);
  const [candidates, setCandidates] = useState<ReimbursementCandidate[]>([]);
  const [picked, setPicked] = useState<ReimbursementCandidate | null>(null);
  const [busy, setBusy] = useState(false);

  // Reject dialog
  const [rejectFor, setRejectFor] = useState<ReimbursementItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const isEmployee = !!hrMe?.employee;
  const canApprove = !!hrMe?.isHrAdmin || !!hrMe?.isManager;

  const refresh = useCallback(async () => {
    try {
      const [mineRes, approvalsRes] = await Promise.all([
        isEmployee ? myReimbursements(nip) : Promise.resolve([]),
        canApprove ? reimbursementApprovals(nip) : Promise.resolve([]),
      ]);
      setMine(mineRes);
      setApprovals(approvalsRes);
    } catch (error) {
      setMine([]);
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    }
  }, [nip, isEmployee, canApprove, myReimbursements, reimbursementApprovals]);

  useEffect(() => { refresh(); }, [refresh]);

  const openWizard = () => {
    setSearchNip('');
    setSearchGross('');
    setSearchDate('');
    setByDate(false);
    setSearched(false);
    setCandidates([]);
    setPicked(null);
    setWizardOpen(true);
  };

  const handleSearch = async () => {
    const gross = Number(searchGross.replace(',', '.'));
    if (!(gross > 0)) {
      toast.error(t('employees.validation.required', { label: t('hr.reimbursements.searchGross') }));
      return;
    }
    if (!byDate && !searchNip.trim()) {
      toast.error(t('employees.validation.required', { label: t('hr.reimbursements.searchNip') }));
      return;
    }
    if (byDate && !searchDate) {
      toast.error(t('employees.validation.required', { label: t('hr.reimbursements.searchIssueDate') }));
      return;
    }
    setBusy(true);
    setPicked(null);
    try {
      const rows = await searchReimbursements(nip, byDate
        ? { issueDate: searchDate, gross }
        : { nip: searchNip.trim(), gross });
      setCandidates(rows);
      setSearched(true);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await createReimbursement(nip, picked.invoiceId);
      toast.success(t('hr.reimbursements.submitted'));
      setWizardOpen(false);
      refresh();
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (item: ReimbursementItem) => {
    try {
      await approveReimbursement(nip, item.id);
      toast.success(t('hr.reimbursements.approved'));
      refresh();
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    }
  };

  const handleReject = async () => {
    if (!rejectFor || !rejectReason.trim()) return;
    setBusy(true);
    try {
      await rejectReimbursement(nip, rejectFor.id, rejectReason.trim());
      toast.success(t('hr.reimbursements.rejected'));
      setRejectFor(null);
      setRejectReason('');
      refresh();
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (mine === null) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      {canApprove && approvals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('hr.reimbursements.approvalsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('hr.reimbursements.colEmployee')}</TableHead>
                  <TableHead>{t('hr.reimbursements.colInvoice')}</TableHead>
                  <TableHead>{t('hr.reimbursements.colVendor')}</TableHead>
                  <TableHead className="text-right">{t('hr.reimbursements.colAmount')}</TableHead>
                  <TableHead>{t('hr.reimbursements.colRequested')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvals.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="py-2 font-medium">{item.employeeName}</TableCell>
                    <TableCell className="py-2">{item.invoiceNumber ?? '—'}</TableCell>
                    <TableCell className="py-2">{item.vendorName ?? '—'}</TableCell>
                    <TableCell className="py-2 text-right tabular-nums">{formatPln(item.amountPln)}</TableCell>
                    <TableCell className="py-2">{formatDateMedium(item.requestedAt.slice(0, 10))}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex justify-end gap-1.5">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" className="h-7">
                              <Check className="size-3.5" />
                              {t('hr.reimbursements.approve')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('hr.reimbursements.approveConfirmTitle')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('hr.reimbursements.approveConfirmBody', {
                                  employee: item.employeeName,
                                  amount: formatPln(item.amountPln),
                                })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleApprove(item)}>
                                {t('hr.reimbursements.approve')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-rose-600 dark:text-rose-400"
                          onClick={() => { setRejectReason(''); setRejectFor(item); }}
                        >
                          <X className="size-3.5" />
                          {t('hr.reimbursements.reject')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {isEmployee && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <HandCoins className="text-muted-foreground size-4" />
              {t('hr.reimbursements.myTitle')}
            </CardTitle>
            <Button size="sm" onClick={openWizard}>
              <Plus className="size-4" />
              {t('hr.reimbursements.request')}
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {mine.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-sm">{t('hr.reimbursements.empty')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('hr.reimbursements.colInvoice')}</TableHead>
                    <TableHead>{t('hr.reimbursements.colVendor')}</TableHead>
                    <TableHead className="text-right">{t('hr.reimbursements.colAmount')}</TableHead>
                    <TableHead>{t('hr.reimbursements.colRequested')}</TableHead>
                    <TableHead>{t('hr.reimbursements.colStatus')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mine.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="py-2 font-medium">{item.invoiceNumber ?? '—'}</TableCell>
                      <TableCell className="py-2">{item.vendorName ?? '—'}</TableCell>
                      <TableCell className="py-2 text-right tabular-nums">{formatPln(item.amountPln)}</TableCell>
                      <TableCell className="py-2">{formatDateMedium(item.requestedAt.slice(0, 10))}</TableCell>
                      <TableCell className="py-2">
                        <StatusChip status={item.status} />
                        {item.status === 'rejected' && item.rejectedReason && (
                          <span className="text-muted-foreground ml-2 text-xs italic">
                            {t('hr.reimbursements.rejectedReason', { reason: item.rejectedReason })}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Request wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('hr.reimbursements.searchTitle')}</DialogTitle>
            <DialogDescription>
              {byDate ? t('hr.reimbursements.searchHintDate') : t('hr.reimbursements.searchHintNip')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {byDate ? (
                <div className="space-y-1.5">
                  <Label htmlFor="reimb-date">{t('hr.reimbursements.searchIssueDate')}</Label>
                  <Input
                    id="reimb-date"
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="reimb-nip">{t('hr.reimbursements.searchNip')}</Label>
                  <Input
                    id="reimb-nip"
                    value={searchNip}
                    onChange={(e) => setSearchNip(e.target.value)}
                    placeholder="1234567890"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="reimb-gross">{t('hr.reimbursements.searchGross')}</Label>
                <Input
                  id="reimb-gross"
                  inputMode="decimal"
                  value={searchGross}
                  onChange={(e) => setSearchGross(e.target.value)}
                  placeholder="123,45"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs underline"
                onClick={() => { setByDate((v) => !v); setSearched(false); setCandidates([]); }}
              >
                {byDate ? t('hr.reimbursements.searchHintNip') : t('hr.reimbursements.searchByDate')}
              </button>
              <Button size="sm" disabled={busy} onClick={handleSearch}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
                {t('hr.reimbursements.searchButton')}
              </Button>
            </div>

            {searched && candidates.length === 0 && (
              <p className="text-muted-foreground text-sm">{t('hr.reimbursements.searchEmpty')}</p>
            )}
            {candidates.length > 0 && (
              <ul className="divide-y rounded-lg border">
                {candidates.map((c) => (
                  <li key={c.invoiceId}>
                    <button
                      type="button"
                      onClick={() => setPicked(c)}
                      className={cn(
                        'hover:bg-muted/60 flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm',
                        picked?.invoiceId === c.invoiceId && 'bg-muted',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{c.invoiceNumber ?? '—'}</span>
                        <span className="text-muted-foreground block truncate text-xs">
                          {c.vendorName ?? '—'}{c.issueDate ? ` · ${formatDateMedium(c.issueDate)}` : ''}
                        </span>
                      </span>
                      <span className="shrink-0 text-right tabular-nums">
                        {c.gross?.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} {c.currency ?? 'PLN'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {picked && (
              <div className="bg-muted/40 space-y-1 rounded-lg border p-3 text-sm">
                <p className="font-medium">{t('hr.reimbursements.confirmTitle')}</p>
                <p>
                  {t('hr.reimbursements.reimbursedAmount')}:{' '}
                  <span className="font-semibold tabular-nums">
                    {picked.amountPln !== null ? formatPln(picked.amountPln) : '—'}
                  </span>
                </p>
                {picked.currency && picked.currency !== 'PLN' && (
                  <p className="text-muted-foreground text-xs">{t('hr.reimbursements.fxNote')}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWizardOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy || !picked} onClick={handleSubmit}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('hr.reimbursements.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(open) => !open && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('hr.reimbursements.rejectTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">{t('hr.reimbursements.rejectReason')}</Label>
            <Textarea
              id="reject-reason"
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy || !rejectReason.trim()}
              onClick={handleReject}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('hr.reimbursements.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
